import CardContainer from '@/components/cards/CardContainer';
import BarChart from '@/components/charts/BarChart';
import BarChartSkeleton from '@/components/charts/BarChartSkeleton';
import Container, { Column } from '@/components/container/Container';
import LoadingProgressBar from '@/components/utils/LoadingProgressBar';
import { addDays, addMonths, addWeeks, format, isBefore } from 'date-fns';
import _ from 'lodash';
import resolveConfig from 'tailwindcss/resolveConfig';
import themeColor from '../../../../styles/colors.json';
import tailwindConfig from '../../../../tailwind.config';
import ChartLabel from './ChartLabel';
import EmptyScreen from './EmptyScreen';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { GetInsightsRequest, Insights4SpendingDto } from '../views/InsightsScreen';
import moment from 'moment';
import { axiosInstance } from '@/services/axios.service';
import { GenericResponse } from '@/common/common_utils';
import { ENDPOINTS } from '@/constants/endpoints';


type DateRange = {
	start: Date | null;
	end: Date | null;
};

type Props = {
	date: string | number;
	title: string;
	dateRange: DateRange | null;
	viewMode: 'week' | 'day';
	setViewMode: React.Dispatch<React.SetStateAction<'week' | 'day'>>;
	program: string;
};

const SpendingChartSection = ({
	date,
	title,
	dateRange,
	viewMode,
	setViewMode,
	program,
	...props
}: Props) => {

	const groupBy = (date == 'today' ? 'hour' : (viewMode == 'day' ? 'day' : 'week'))
	const insightsQ = useQuery({
		queryKey: ['Insights.Spending', date, dateRange, viewMode],
		queryFn: async () => {

			const result = await axiosInstance.post<GenericResponse<Insights4SpendingDto>>(ENDPOINTS['/api/payments/GetInsights4Spending'], { Start: dateRange?.start, End: dateRange?.end, GroupBy: groupBy } as GetInsightsRequest);

			return result.data.Data;
		},
		enabled: !!(dateRange?.start && dateRange?.end),
	});

	const renderBody = () => {
		const hasMode = date !== 'today' && date !== 7;

		const stateTemplate = () => {
			if (!insightsQ.isFetched || !insightsQ.isError)
				return (
					<Column gap="gap-4">
						<Container className="-mt-6">
							<LoadingProgressBar />
						</Container>
						<BarChartSkeleton />
					</Column>
				);

			if (insightsQ.data?.Spending.length == 0) {
				return <EmptyScreen isError={false} height="h-[470px]" />;
			}

			if (insightsQ.isError) {
				return (
					<EmptyScreen
						isError={insightsQ.isError}
						// hasError={hasError}
						height="h-[470px]"
					/>
				);
			}
		};

		if (!insightsQ.isFetched) {
			return stateTemplate();
		}

		let labels: string[];

		if (date == 'today') {
			labels = insightsQ.data?.Spending.map(i => moment(i.Date).format('hh:mm')) ?? [];
		} else if (date == 7 || viewMode == 'day') {
			labels = insightsQ.data?.Spending.map(i => moment(i.Date).format('DD/YY')) ?? [];
		} else {//'week'
			labels = insightsQ.data?.Spending.map(i => {
				const startDate = moment(i.Date);
				const endDate = moment(i.Date).add(6, 'days');
				return `${startDate.format('MMM DD')} - ${endDate.format('DD')}`;
			}) ?? [];
		}


		const barData = {
			labels,
			datasets: [
				{
					label: 'Travel',
					backgroundColor: [themeColor.tertiary],
					borderColor: [themeColor.tertiary],
					data: insightsQ.data?.Spending.map(i => i.Value)
				}
			],
		};
		const maxY = (insightsQ.data?.Spending.reduce((a, b) => a < b.Value ? b.Value : a, 0) ?? 100);
		const fullConfig = resolveConfig(tailwindConfig);

		const axesLabelStyle = {
			display: true,
			font: {
				size: 16,
				family: fullConfig.theme.fontFamily.poppins,
				weight: 600,
			},
			color: themeColor.primaryFixedDim,
			padding: 10,
		};

		const ticksStyleX = {
			display: true,
			font: {
				size: 8,
				family: fullConfig.theme.fontFamily.poppins,
				weight: 400,
				lineHeight: 2,
			},
			color: themeColor.secondary,
		};

		const ticksStyleY = {
			display: true,
			stepSize: 200,
			font: {
				size: 10,
				family: fullConfig.theme.fontFamily.poppins,
				weight: 400,
			},
			color: themeColor.chartTickY,
		};

		const barOptions = {
			maintainAspectRatio: false,
			aspectRatio: 0.8,
			plugins: {
				legend: {
					display: false,
				},
			},
			scales: {
				x: {
					ticks: {
						...ticksStyleX,
					},
					grid: {
						display: false,
					},
					title: {
						text: title,
						...axesLabelStyle,
					},
				},
				y: {
					min: 0,
					max: maxY,
					ticks: {
						...ticksStyleY,
					},
					grid: {
						display: false,
					},
					title: {
						text: 'Amount in Thousands (₱)',
						...axesLabelStyle,
					},
				},
			},
		};

		return (
			<div className="flex flex-col space-y-7">
				<div className="flex flex-col space-y-2">
					<ChartLabel
						value={`₱${insightsQ.data?.Spending.reduce((a, b) => a + b.Value, 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
						label={`Spend ${_.lowerFirst(title)}`}
						hasMode={hasMode}
						viewMode={viewMode}
						setViewMode={setViewMode}
					/>
					<div className="flex gap-3 items-center">
						<div className="bg-info h-[18px] w-[18px]" />
						<p className="text-label-semibold text-neutral50 font-poppins">
							{_.upperFirst(program)}
						</p>
					</div>
				</div>

				<BarChart data={barData} options={barOptions} />
			</div>
		);

	}

	return (
		<CardContainer>
			<Container className="p-6 h-full bg-surfaceBright rounded-[24px]">
				{renderBody()}
			</Container>
		</CardContainer>
	);
};

export default SpendingChartSection;
